import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import type { PaginationMeta } from '@/types';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useMemo } from 'react';

interface PaginationControlsProps {
    pagination: PaginationMeta;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
    perPageOptions?: number[];
    loading?: boolean;
    className?: string;
}

const DEFAULT_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export function PaginationControls({
    pagination,
    onPageChange,
    onPerPageChange,
    perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
    loading = false,
    className,
}: PaginationControlsProps) {
    const { t } = useTranslations();
    const { current_page, last_page, per_page, total, from, to } = pagination;
    const canGoPrev = current_page > 1;
    const canGoNext = current_page < last_page;

    const displayedPages = useMemo(() => {
        const pages: (number | 'ellipsis')[] = [];
        const windowSize = 2;

        const startPage = Math.max(1, current_page - windowSize);
        const endPage = Math.min(last_page, current_page + windowSize);

        if (startPage > 1) {
            pages.push(1);
            if (startPage > 2) {
                pages.push('ellipsis');
            }
        }

        for (let i = startPage; i <= endPage; i += 1) {
            pages.push(i);
        }

        if (endPage < last_page) {
            if (endPage < last_page - 1) {
                pages.push('ellipsis');
            }
            pages.push(last_page);
        }

        return pages;
    }, [current_page, last_page]);

    const handlePageChange = (page: number) => {
        if (page < 1 || page > last_page || page === current_page) {
            return;
        }
        onPageChange(page);
    };

    return (
        <div className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${className ?? ''}`}>
            <div className="text-sm text-muted-foreground">
                {t('work_orders.pagination.showing', 'Showing')} <span className="font-medium text-foreground">{from ?? 0}-{to ?? 0}</span> {t('work_orders.pagination.of', 'of')}{' '}
                <span className="font-medium text-foreground">{total}</span>
            </div>

            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                {onPerPageChange && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {t('work_orders.pagination.rows_per_page', 'Rows per page:')}
                        <Select value={String(per_page)} onValueChange={(value) => onPerPageChange(Number(value))}>
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {perPageOptions.map((option) => (
                                    <SelectItem key={option} value={String(option)}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(1)}
                        disabled={!canGoPrev || loading}
                        aria-label={t('work_orders.pagination.first_page', 'First page')}
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(current_page - 1)}
                        disabled={!canGoPrev || loading}
                        aria-label={t('work_orders.pagination.previous_page', 'Previous page')}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="hidden md:flex items-center gap-1">
                        {displayedPages.map((page, index) =>
                            page === 'ellipsis' ? (
                                <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                                    …
                                </span>
                            ) : (
                                <Button
                                    key={page}
                                    variant={page === current_page ? 'default' : 'outline'}
                                    size="icon"
                                    onClick={() => handlePageChange(page)}
                                    disabled={loading}
                                >
                                    {page}
                                </Button>
                            ),
                        )}
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(current_page + 1)}
                        disabled={!canGoNext || loading}
                        aria-label={t('work_orders.pagination.next_page', 'Next page')}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(last_page)}
                        disabled={!canGoNext || loading}
                        aria-label={t('work_orders.pagination.last_page', 'Last page')}
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
